/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useEffect } from 'react';
import { SVG } from 'components/Asset/SVG';
import { FlexBox } from 'components/Containers/FlexBox';
import { Box } from '@mui/material';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { ThemePalette } from 'mui.theme';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  convertStdDateString,
  convertUtcToDisplayTime,
} from 'utils/helpers/dateTime';
import { getLabelWrtUnitSystem } from 'utils/helpers/unitSystem';
import {
  getPointMatch,
  roundTimestampsToHour,
  prepareDataForCsv,
  generateTpmSNotificationCSV,
  getHistoryMatch,
  formatHistoryTs,
  getToolTipText,
} from 'views/TirePressure/util';
import type { Asset } from 'types/asset.types';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const Chart = ({
  enableDarkTheme,
  data,
  colors,
  allowedProfiles,
  unitSystem,
  tireCodes,
  metricType,
  notifications,
  startDate,
  endDate,
  selectedAsset,
  trigger,
  onComplete,
  chartKey,
  makeModelDetails,
  tirePatterns,
  customerId,
  selectedRange,
}: {
  enableDarkTheme: boolean;
  data: any;
  colors: string[];
  allowedProfiles: string[];
  unitSystem: string;
  tireCodes: any;
  metricType: string;
  setMetricType: any;
  notifications: any[];
  startDate: Date;
  endDate: Date;
  selectedAsset: Asset | undefined;
  trigger: number;
  onComplete: any;
  chartKey: string;
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
  selectedRange: string;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const fitmentHistory = data.fitmentHistory || [];
  const dataToUse =
    (metricType === 'Temperature' ? data.temperatureData : data.pressureData) ||
    {};
  const dataLines = Object.keys(dataToUse);
  const dataValues = Object.values(dataToUse);

  const formatTimestamp = (timestamp: number): string => {
    const formattedDate = convertUtcToDisplayTime(
      timestamp,
      preferredTimeDisplayMode,
      timezone,
      DATE_TIME_FORMAT
    );
    return formattedDate;
  };

  const formatTimestampWithHour = (timestamp: number): string => {
    const formattedDate = convertUtcToDisplayTime(
      timestamp,
      preferredTimeDisplayMode,
      timezone,
      DATE_TIME_FORMAT
    );
    return formattedDate;
  };

  const formatValue = (value: any) => {
    if (typeof value === 'number' && value % 1 !== 0) {
      return value.toFixed(2);
    }
    return value;
  };
  const textColor = palette.textSecondary;

  const formattedNotifications = roundTimestampsToHour(notifications);
  const formattedHistory = formatHistoryTs(fitmentHistory, dataToUse, timezone);

  const CustomDot = ({
    cx,
    cy,
    fill,
    payload,
    lineName,
    r,
    isLessThanTenItems,
    isLastPayload,
    allPayloads,
  }: any) => {
    const pointMatch = getPointMatch(
      formattedNotifications,
      payload,
      lineName,
      selectedRange,
      allPayloads
    );

    const historyMatch = getHistoryMatch(formattedHistory, payload, lineName);

    const getUnmatchedNotifications = () => {
      if (!isLastPayload) return [];

      const matchedNotifications = new Set();

      allPayloads.forEach((p: any) => {
        const match = getPointMatch(formattedNotifications, p, lineName);
        if (match) {
          matchedNotifications.add(match.id || match);
        }
      });

      const lastPayloadTime = payload.ts;

      return formattedNotifications.filter((notification) => {
        const isUnmatched = !matchedNotifications.has(
          notification.id || notification
        );
        const belongsToLine =
          notification?.tireCode?.toLowerCase() === lineName?.toLowerCase();
        const isAfterLastPayload =
          new Date(notification.hourlyTs).getTime() > lastPayloadTime;

        return isUnmatched && belongsToLine && isAfterLastPayload;
      });
    };

    const unmatchedNotifications = getUnmatchedNotifications();

    if (pointMatch && historyMatch.length > 0) {
      return (
        <g>
          {/* Point match icon on the left */}
          <g>
            <SVG
              name={
                pointMatch.notificationType?.toLowerCase().includes('fast')
                  ? 'leakageFast'
                  : pointMatch.notificationType?.toLowerCase().includes('slow')
                  ? 'leakageSlow'
                  : pointMatch.notificationType === 'inflate'
                  ? 'infoBlink'
                  : 'bellBlink'
              }
              x={cx - 7}
              y={cy - 21}
              width={14}
              height={14}
              {...(!(
                pointMatch.notificationType?.toLowerCase().includes('fast') ||
                pointMatch.notificationType?.toLowerCase().includes('slow')
              ) && {
                fill,
                stroke: fill,
                style: { color: fill },
              })}
            />
          </g>
          {/* History match wheel tire icon on the right - not clickable */}
          <g>
            <SVG
              name="wheelsTire"
              x={cx + 2}
              y={cy - 18}
              width={12}
              height={12}
              fill={fill}
              stroke={fill}
              style={{ color: fill }}
            />
          </g>
        </g>
      );
    }

    if (pointMatch) {
      return (
        <g>
          <SVG
            name={
              pointMatch.notificationType?.toLowerCase().includes('fast')
                ? 'leakageFast'
                : pointMatch.notificationType?.toLowerCase().includes('slow')
                ? 'leakageSlow'
                : pointMatch.notificationType === 'inflate'
                ? 'infoBlink'
                : 'bellBlink'
            }
            x={cx - 7}
            y={cy - 21}
            width={14}
            height={14}
            {...(!(
              pointMatch.notificationType?.toLowerCase().includes('fast') ||
              pointMatch.notificationType?.toLowerCase().includes('slow')
            ) && {
              fill,
              stroke: fill,
              style: { color: fill },
            })}
          />
        </g>
      );
    }

    if (historyMatch.length > 0) {
      return (
        <g>
          <SVG
            name="wheelsTire"
            x={cx - 6}
            y={cy - 18}
            width={12}
            height={12}
            fill={fill}
            stroke={fill}
            style={{ color: fill }}
          />
        </g>
      );
    }

    if (isLastPayload && unmatchedNotifications.length > 0) {
      return (
        <g>
          {unmatchedNotifications.map((notification, index) => {
            const offsetX = 1 + index * 4;

            return (
              <g key={notification.id || index}>
                <SVG
                  name={
                    notification.notificationType
                      ?.toLowerCase()
                      .includes('fast')
                      ? 'leakageFast'
                      : notification.notificationType
                          ?.toLowerCase()
                          .includes('slow')
                      ? 'leakageSlow'
                      : notification.notificationType === 'inflate'
                      ? 'infoBlink'
                      : 'bellBlink'
                  }
                  x={cx - offsetX - 7}
                  y={cy - 21}
                  width={14}
                  height={14}
                  {...(!(
                    notification.notificationType
                      ?.toLowerCase()
                      .includes('fast') ||
                    notification.notificationType
                      ?.toLowerCase()
                      .includes('slow')
                  ) && {
                    fill,
                    stroke: fill,
                    style: { color: fill },
                  })}
                />
              </g>
            );
          })}
        </g>
      );
    }

    return null;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const tooltipStyle = {
        backgroundColor: enableDarkTheme ? '#2D3748' : '#FFFFFF',
        border: enableDarkTheme ? '1px solid #4A5568' : '1px solid #EAEAEA',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
      };

      const labelStyle = {
        margin: '0 0 8px 0',
        paddingBottom: '10px',
        fontSize: '12px',
        fontWeight: 'bold',
        color: enableDarkTheme ? '#E2E8F0' : '#4A5568',
        borderBottom: enableDarkTheme
          ? '1px solid #4A5568'
          : '1px solid #EAEAEA',
      };

      const entryContainerStyle = {
        marginBottom: '6px',
      };

      const valueStyle = {
        fontWeight: 'bold',
        color: enableDarkTheme ? '#FFFFFF' : '#1A202C',
      };

      return (
        <div style={tooltipStyle}>
          <p style={labelStyle}>{formatTimestampWithHour(label)}</p>
          {payload.map((entry: any, index: number) => {
            const pointMatch = getPointMatch(
              formattedNotifications,
              entry.payload,
              entry.name,
              selectedRange,
              dataToUse[entry.name]
            );
            const historyMatch = getHistoryMatch(
              formattedHistory,
              entry.payload,
              entry.name
            );
            const keyPair = tireCodes[entry.name] ?? entry.name;
            return (
              <div key={index} style={entryContainerStyle}>
                <span
                  style={{
                    color: entry.color,
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      marginRight: '10px',
                    }}
                  >
                    {keyPair}:
                  </span>
                  <span style={valueStyle}>{formatValue(entry.value)}</span>
                  {pointMatch && (
                    <span
                      style={{
                        marginLeft: '10px',
                        color: palette.textPrimary,
                        fontSize: '10px',
                        textTransform: 'capitalize',
                      }}
                    >
                      <>{`(${pointMatch.notificationType})`}</>
                    </span>
                  )}
                  {historyMatch.length > 0 && (
                    <span
                      style={{
                        marginLeft: '10px',
                        color: palette.textPrimary,
                        fontSize: '10px',
                        textTransform: 'capitalize',
                      }}
                    >
                      {getToolTipText(historyMatch, tireCodes)}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  function handleGenerateCsv() {
    const pressureData = Object.values(data.pressureData);
    const temperatureData = Object.values(data.temperatureData);
    const csvData = prepareDataForCsv(
      pressureData,
      temperatureData,
      dataLines,
      tireCodes,
      formattedNotifications,
      selectedAsset,
      preferredTimeDisplayMode,
      timezone,
      formattedHistory,
      data.tireCurrentStatus,
      data.tireCurrentTemperature,
      customerId,
      makeModelDetails,
      tirePatterns,
      selectedRange
    );

    const title = `${
      selectedAsset?.bumperNumber
    } TPMS Notification Report - ${convertStdDateString(
      startDate
    )} to ${convertStdDateString(endDate)}`;

    const fileName = `${selectedAsset?.bumperNumber}_tpms_notification_report.csv`;

    const baseColumns = [
      { key: 'bumperNumber', label: t('device_id') },
      { key: 'tireCodeName', label: t('tire_code') },
      { key: 'dateTime', label: 'Date Time' },
    ];

    const pressureColumns = [
      {
        key: 'pressureValue',
        label: `Pressure (${unitSystem === 'metric' ? 'KPA' : 'PSI'})`,
      },
    ];

    const temperatureColumns = [
      {
        key: 'temperatureValue',
        label: `Temperature (${unitSystem === 'metric' ? 'C' : 'F'})`,
      },
    ];

    const notificationColumn = [
      { key: 'notificationType', label: t('remark') },
    ];

    generateTpmSNotificationCSV(
      csvData,
      fileName,
      [
        ...baseColumns,
        ...pressureColumns,
        ...temperatureColumns,
        ...notificationColumn,
      ],
      title
    );
  }

  useEffect(() => {
    if (trigger > 0) {
      handleGenerateCsv();
      onComplete();
    }
  }, [trigger]);

  return (
    <Box>
      <FlexBox
        sx={{
          maxWidth: 'inherit',
          height: '500px',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            margin={{
              top: 0,
              right: 20,
              left: 10,
              bottom: 50,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={'#25364c'}
              vertical={false}
            />
            <XAxis
              dataKey="ts"
              tick={{ fontSize: 9, fill: textColor }}
              allowDuplicatedCategory={false}
              tickFormatter={formatTimestamp}
              type="number" // Set x-axis type to 'number'
              domain={['dataMin', 'datamax']} // Set x-axis domain automatically
              angle={-65}
              textAnchor="end"
              // interval={0}
              minTickGap={10}
              tickCount={30}
            />
            <YAxis
              tick={{ fontSize: 11, fill: textColor }}
              label={{
                value: `${metricType} (${
                  metricType === 'Pressure'
                    ? unitSystem === 'metric'
                      ? 'kpa'
                      : 'psi'
                    : getLabelWrtUnitSystem('C', unitSystem).toLowerCase()
                })`,
                angle: 270,
                position: 'insideLeft',
                fontSize: 10,
                textAnchor: 'middle',
                dy: 50,
                dx: -5,
              }}
              domain={[
                0,
                metricType === 'Pressure'
                  ? unitSystem === 'metric'
                    ? 1200
                    : 180
                  : 180,
              ]}
            />
            <Tooltip content={<CustomTooltip />} />
            {allowedProfiles.map((item) => {
              const generalIndex = dataLines.findIndex((e) => item === e);

              const isLessThanTenItems = Array.isArray(dataValues[generalIndex])
                ? (dataValues[generalIndex] as unknown[]).length <= 5
                : false;

              return (
                <Line
                  key={item}
                  data={dataValues[generalIndex]}
                  type="linear"
                  dataKey={chartKey}
                  stroke={colors[generalIndex]}
                  fill={colors[generalIndex]}
                  activeDot={{ r: 1 }}
                  fillOpacity={0.3}
                  strokeOpacity={1}
                  dot={(dotProps) => (
                    <CustomDot
                      {...dotProps}
                      fill={colors[generalIndex]}
                      lineName={item}
                      isLessThanTenItems={isLessThanTenItems}
                      isLastPayload={
                        dotProps.index ===
                        (dataValues[generalIndex] as any[]).length - 1
                      }
                      allPayloads={dataValues[generalIndex]}
                    />
                  )}
                  name={item}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </FlexBox>
    </Box>
  );
};
